import type {
	FieldTextBinding,
	HeadingElement,
	TextBinding,
	TextElement,
} from "../types";

/** Post-type custom field offered as a heading/text dynamic source (host-filtered). */
export type DynamicFieldSource = {
	slug: string;
	label: string;
	fieldType: string;
};

/** Resolved page-level strings for heading/text dynamic bindings (host + builder). */
export type PageDynamicText = {
	excerpt?: string | null;
	pageTitle?: string | null;
	metaTitle?: string | null;
	metaDescription?: string | null;
};

const FIELD_BINDING_PREFIX = "field:" as const;

const BUILT_IN_DYNAMIC_TEXT_BINDINGS = new Set<
	Exclude<BuiltInTextBinding, "static">
>(["excerpt", "page_title", "meta_title", "meta_description"]);

type BuiltInTextBinding = Exclude<TextBinding, FieldTextBinding>;

const ELIGIBLE_FIELD_TEXT_TYPES = new Set(["text", "textarea", "markdown"]);

export function isFieldTextBinding(
	binding: string | undefined
): binding is FieldTextBinding {
	return (
		typeof binding === "string" &&
		binding.startsWith(FIELD_BINDING_PREFIX) &&
		binding.length > FIELD_BINDING_PREFIX.length
	);
}

export function fieldSlugFromTextBinding(binding: FieldTextBinding): string {
	return binding.slice(FIELD_BINDING_PREFIX.length);
}

/** Coerce an entry `field_values[slug]` to plain string for text bindings. */
export function resolveFieldValueAsString(
	fieldValues: Record<string, unknown> | null | undefined,
	slug: string,
	fieldType?: string
): string {
	if (!(fieldValues && slug.trim())) {
		return "";
	}
	if (fieldType && !ELIGIBLE_FIELD_TEXT_TYPES.has(fieldType)) {
		return "";
	}
	const raw = fieldValues[slug];
	if (raw == null) {
		return "";
	}
	if (typeof raw === "string") {
		return raw.trim();
	}
	if (typeof raw === "number" || typeof raw === "boolean") {
		return String(raw);
	}
	return "";
}

export function isDynamicTextBinding(
	binding: TextBinding | string | undefined
): binding is Exclude<TextBinding, "static"> {
	if (typeof binding !== "string" || binding === "static") {
		return false;
	}
	if (isFieldTextBinding(binding)) {
		return true;
	}
	return BUILT_IN_DYNAMIC_TEXT_BINDINGS.has(
		binding as Exclude<BuiltInTextBinding, "static">
	);
}

export function effectiveTextBinding(
	el: Pick<HeadingElement | TextElement, "textBinding">
): TextBinding {
	const raw = el.textBinding;
	if (raw && isDynamicTextBinding(raw)) {
		return raw;
	}
	return "static";
}

/** Read `textBinding` from persisted preview nodes (`props` or legacy top-level). */
export function textBindingFromPersistedNode(
	node: { textBinding?: string },
	props: { textBinding?: string } | null | undefined
): string | undefined {
	const propsBinding = props?.textBinding;
	if (typeof propsBinding === "string" && propsBinding.length > 0) {
		return propsBinding;
	}
	const topBinding = node.textBinding;
	return typeof topBinding === "string" && topBinding.length > 0
		? topBinding
		: undefined;
}

/** Editor / preview hints when a dynamic source is unset. */
export const BUILDER_EXCERPT_PLACEHOLDER =
	"Page excerpt (not set — add in Page settings)";

export const BUILDER_PAGE_TITLE_PLACEHOLDER =
	"Page title (not set — add in Page settings)";

export const BUILDER_META_TITLE_PLACEHOLDER = "Meta title (not set)";

export const BUILDER_META_DESCRIPTION_PLACEHOLDER =
	"Meta description (not set)";

const PLACEHOLDER_BY_BINDING: Record<
	Exclude<BuiltInTextBinding, "static">,
	string
> = {
	excerpt: BUILDER_EXCERPT_PLACEHOLDER,
	page_title: BUILDER_PAGE_TITLE_PLACEHOLDER,
	meta_title: BUILDER_META_TITLE_PLACEHOLDER,
	meta_description: BUILDER_META_DESCRIPTION_PLACEHOLDER,
};

function fieldBindingPlaceholder(slug: string): string {
	return `Field "${slug}" (not set)`;
}

function placeholderForBinding(
	binding: Exclude<TextBinding, "static">
): string {
	if (isFieldTextBinding(binding)) {
		return fieldBindingPlaceholder(fieldSlugFromTextBinding(binding));
	}
	return PLACEHOLDER_BY_BINDING[binding];
}

function valueForBinding(
	binding: Exclude<TextBinding, "static">,
	pageDynamicText: PageDynamicText | null | undefined,
	fieldValues?: Record<string, unknown> | null
): string {
	if (isFieldTextBinding(binding)) {
		return resolveFieldValueAsString(
			fieldValues,
			fieldSlugFromTextBinding(binding)
		);
	}
	const text = pageDynamicText ?? {};
	switch (binding) {
		case "excerpt":
			return text.excerpt?.trim() ?? "";
		case "page_title":
			return text.pageTitle?.trim() ?? "";
		case "meta_title":
			return text.metaTitle?.trim() ?? "";
		case "meta_description":
			return text.metaDescription?.trim() ?? "";
		default:
			return "";
	}
}

/** Merge host `pageDynamicText` with legacy `pageExcerpt` prop. */
export function mergePageDynamicText(
	pageDynamicText: PageDynamicText | null | undefined,
	pageExcerpt?: string | null
): PageDynamicText {
	const merged: PageDynamicText = { ...(pageDynamicText ?? {}) };
	if (pageExcerpt != null && merged.excerpt === undefined) {
		merged.excerpt = pageExcerpt;
	}
	return merged;
}

export type ResolveBoundTextContentOptions = {
	forEditor?: boolean;
	/** Entry `field_values` for `field:{slug}` bindings. */
	fieldValues?: Record<string, unknown> | null;
};

export function resolveBoundTextContent(
	el: Pick<HeadingElement | TextElement, "content" | "textBinding" | "type">,
	pageDynamicText: PageDynamicText | null | undefined,
	options?: ResolveBoundTextContentOptions
): string {
	if (el.type !== "heading" && el.type !== "text") {
		return (el as { content?: string }).content ?? "";
	}
	const binding = effectiveTextBinding(el);
	if (binding === "static") {
		const content = el.content?.trim();
		return content || "";
	}
	const resolved = valueForBinding(
		binding,
		pageDynamicText,
		options?.fieldValues
	);
	if (resolved) {
		return resolved;
	}
	return options?.forEditor === true ? placeholderForBinding(binding) : "";
}
